import {
  Action,
  IAgentRuntime,
  Memory,
  State,
  elizaLogger,
  HandlerCallback,
} from '@elizaos/core';
import { ethers } from 'ethers';
import { initCotiWalletProvider } from '../providers/wallet';
import { contractsApi, tradeMatcher, OrdersMap, MatchType } from '@bancor/carbon-sdk';


const carbonControllerAbi = [
  'function tradeBySourceAmount(address sourceToken, address targetToken, tuple(uint128 strategyId, uint128 amount)[] calldata tradeActions, uint256 deadline, uint128 minReturn) payable returns (uint128)',
  'function strategiesByPair(address sourceToken, address targetToken, uint256 startIndex, uint256 endIndex) view returns (uint128[] memory strategyIds)',
];

const erc20Abi = [
  'function approve(address spender, uint256 amount) returns (bool)',
  'function allowance(address owner, address spender) view returns (uint256)',
  'function decimals() view returns (uint8)',
  'function symbol() view returns (string)',
];

export const swapAction: Action = {
  name: 'SWAP_TOKENS',
  similes: ['EVM_SWAP_TOKENS', 'SWAP', 'TRADE_TOKENS'],
  description: 'Swaps one token for another using Carbon DeFi.',
  validate: async (runtime: IAgentRuntime, message: Memory) => {
    const text = message.content?.text || "";
    const isSwapQuery = /\b(swap|trade|convert|exchange)\b/i.test(text);
    const hasAmount = /\b\d+(\.\d+)?\b/.test(text);
    const hasTokens = /([a-zA-Z0-9_.-]+)\s*(?:for|to|\/)\s*([a-zA-Z0-9_.-]+)/i.test(text);
    const hasAddress = /0x[a-fA-F0-9]{40}/i.test(text);
    const isValid = isSwapQuery && hasAmount && (hasTokens || hasAddress);
    return isValid;
  },
  handler: async (
    runtime: IAgentRuntime,
    message: Memory,
    state: State | undefined,
    options: any = {},
    callback?: HandlerCallback
  ) => {
    let { sourceTokenAddress, targetTokenAddress, amount, minReturn } = message.content as any;

    if (!sourceTokenAddress || !targetTokenAddress || !amount) {
      const text = message.content?.text || "";
      const amountMatch = text.match(/([\d.]+)/);
      amount = amountMatch ? amountMatch[1] : '0';

      const tokenMap: { [key: string]: string } = {
        'gCOTI': '0x7637C7838EC4Ec6b85080F28A678F8E234bB83D1',
        'USDC.e': '0xf1Feebc4376c68B7003450ae66343Ae59AB37D3C',
        'Pengo': '0x659AD6d1F7353Df13Dec552cc05c9c15AfdD04e8',
        'BIGM': '0xFb610a80C8f236C3260834de186C6CFD80b8f12f',
        'COTI': '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE',
      };

      const addresses = text.match(/0x[a-fA-F0-9]{40}/ig) || [];
      const tokenMatch = text.match(/([a-zA-Z0-9_.-]+)\s*(?:for|to|\/)\s*([a-zA-Z0-9_.-]+)/i);

      if (tokenMatch) {
        const sourceSymbolRaw = tokenMatch[1];
        const targetSymbolRaw = tokenMatch[2];

        sourceTokenAddress = tokenMap[sourceSymbolRaw] || (addresses.length > 0 ? addresses[0] : undefined);
        targetTokenAddress = tokenMap[targetSymbolRaw] || (addresses.length > 1 ? addresses[1] : undefined);

      } else if (addresses.length >= 2) {
        sourceTokenAddress = addresses[0];
        targetTokenAddress = addresses[1];
      } else {
        // This branch is now handled above, but kept for safety.
        const fallbackTokenMatch = text.match(/([a-zA-Z0-9_.-]+)\s*(?:for|to|\/)\s*([a-zA-Z0-9_.-]+)/i);
        if (fallbackTokenMatch) {
          const sourceSymbol = fallbackTokenMatch[1];
          const targetSymbol = fallbackTokenMatch[2];
          sourceTokenAddress = tokenMap[sourceSymbol];
          targetTokenAddress = tokenMap[targetSymbol];
        }
      }

      if (!sourceTokenAddress || !targetTokenAddress) {
        return {
          text: `Could not find addresses for the tokens in your request.`,
          success: false,
        };
      }

      // Check if trying to swap identical tokens
      if (sourceTokenAddress.toLowerCase() === targetTokenAddress.toLowerCase()) {
        return {
          text: `Cannot swap a token to itself. Source and target tokens are identical.`,
          success: false,
        };
      }
    }

    try {
      const rpcUrl = 'https://mainnet.coti.io/rpc';
      const contractAddress = '0x59f21012B2E9BA67ce6a7605E74F945D0D4C84EA';
      const networkId = 2632500;

      elizaLogger.info(`Connecting to provider: ${rpcUrl}`);
      const provider = new ethers.providers.JsonRpcProvider(rpcUrl);

      const walletProvider = initCotiWalletProvider(runtime);
      const wallet = walletProvider.getWallet();
      if (!wallet) {
        return {
          text: 'Wallet not found. Please configure your wallet first.',
          success: false,
        };
      }
      const connectedWallet = wallet;
      const walletAddress = await connectedWallet.getAddress();

      const NATIVE_COTI_ADDRESS = '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE';
      const sourceIsNative = sourceTokenAddress === NATIVE_COTI_ADDRESS;
      const targetIsNative = targetTokenAddress === NATIVE_COTI_ADDRESS;

      let sourceDecimals, sourceSymbol;
      if (sourceIsNative) {
        sourceDecimals = 18;
        sourceSymbol = 'COTI';
      } else {
        const sourceTokenReader = new ethers.Contract(sourceTokenAddress, erc20Abi, provider);
        sourceDecimals = await sourceTokenReader.decimals();
        sourceSymbol = await sourceTokenReader.symbol();
      }

      let targetDecimals, targetSymbol;
      if (targetIsNative) {
        targetDecimals = 18;
        targetSymbol = 'COTI';
      } else {
        const targetTokenContract = new ethers.Contract(
          targetTokenAddress,
          erc20Abi,
          provider
        );
        targetDecimals = await targetTokenContract.decimals();
        targetSymbol = await targetTokenContract.symbol();
      }

      const amountIn = ethers.utils.parseUnits(amount, sourceDecimals);

      if (sourceIsNative) {
        const balance = await provider.getBalance(walletAddress);
        if (balance.lt(amountIn)) {
          return {
            text: `Insufficient balance. Available: ${ethers.utils.formatUnits(balance, 18)} COTI, Required: ${amount} COTI`,
            success: false,
          };
        }
      }

      if (!sourceIsNative) {
        const sourceTokenReader = new ethers.Contract(sourceTokenAddress, erc20Abi, provider);
        const allowance = await sourceTokenReader.allowance(
          walletAddress,
          contractAddress
        );

        if (allowance.lt(amountIn)) {
          elizaLogger.info(
            `Approving ${amount} ${sourceSymbol} for CarbonController...`
          );
          const contractInterface = new ethers.utils.Interface(erc20Abi);
          const data = contractInterface.encodeFunctionData("approve", [
            contractAddress,
            amountIn
          ]);

          const approveTx = await connectedWallet.sendTransaction({
            to: sourceTokenAddress,
            data: data,
            gasLimit: 100000
          });

          await approveTx.wait();
          elizaLogger.info('Approval successful.');
        }
      }

      const sdk = new contractsApi.ContractsApi(provider as any, {
        carbonControllerAddress: contractAddress,
      });

      elizaLogger.info(`Fetching strategies for ${sourceSymbol} -> ${targetSymbol}...`);
      const strategies = await sdk.reader.strategiesByPair(sourceTokenAddress, targetTokenAddress);

      if (strategies.length === 0) {
        return {
          text: `No liquidity strategies found for ${sourceSymbol} -> ${targetSymbol}`,
          success: false,
        };
      }

      elizaLogger.info(`Found ${strategies.length} strategies. Matching trade...`);
      const ordersMap = strategies.reduce((acc, strategy) => {
        const order = strategy.token0 === sourceTokenAddress ? strategy.order1 : strategy.order0;
        acc[strategy.id.toString()] = order;
        return acc;
      }, {} as OrdersMap);

      const matchOptions = tradeMatcher.matchBySourceAmount(
        ethers.BigNumber.from(amountIn.toString()),
        ordersMap,
        [MatchType.Best]
      );

      const bestMatch = matchOptions[MatchType.Best];

      if (!bestMatch || bestMatch.length === 0) {
        return {
          text: `No liquidity found for the desired trade of ${amount} ${sourceSymbol}`,
          success: false,
        };
      }

      const tradeActions = bestMatch.map(action => ({
        strategyId: action.id,
        amount: action.input,
      }));

      const totalTargetAmount = bestMatch.reduce((acc, action) => acc.add(action.output), ethers.BigNumber.from(0));

      const deadline = Math.floor(Date.now() / 1000) + 60 * 20; // 20 minutes

      const transaction = await sdk.composer.tradeBySourceAmount(
        sourceTokenAddress,
        targetTokenAddress,
        tradeActions,
        deadline.toString(),
        totalTargetAmount.toString()
      );

      elizaLogger.info(
        `Swapping ${amount} ${sourceSymbol} for an estimated ${ethers.utils.formatUnits(totalTargetAmount.toString(), targetDecimals)} ${targetSymbol}...`
      );

            elizaLogger.info('Composed transaction:', transaction);
      elizaLogger.info('Trade actions:', tradeActions);

            const gasPrice = await provider.getGasPrice();

      const tx = await connectedWallet.sendTransaction({
        to: contractAddress,
        data: transaction.data,
        value: sourceIsNative ? amountIn.toString() : undefined,
        gasLimit: 500000, // Add a manual gas limit
        gasPrice: gasPrice.toString(),
      });

      elizaLogger.info(`Transaction sent: ${tx.hash}`);
      await tx.wait();
      elizaLogger.info('Swap successful!');

      const responseText = `Successfully swapped ${amount} ${sourceSymbol} for approximately ${ethers.utils.formatUnits(totalTargetAmount.toString(), targetDecimals)} ${targetSymbol}. Transaction hash: ${tx.hash}`;

      if (callback) {
        await callback({
          success: true,
          transactionHash: tx.hash,
          text: responseText,
        });
      }

      return {
        text: responseText,
        values: {
          transactionHash: tx.hash,
        },
        success: true,
      };
    } catch (error: any) {
      elizaLogger.error('Error in swap action:', error);
      return {
        text: `Failed to swap tokens: ${error.message}`,
        success: false,
      };
    }
  },
};